/** @jsx jsx */
import {
  Button,
  Grid,
  Icon,
  Image,
  List,
  Modal,
  ReloadingOverlay,
  Spacer,
  Spinner,
  TextGroupSmall,
} from "@cd2/components"
import { Form2 } from "@cd2/forms"
import { useSkeem } from "../../providers/SkeemProvider"
import { H2, H3, P } from "@cd2/text"
import { useTheme } from "@cd2/theme"
import { assert, formatDate } from "@cd2/tools"
import { css, jsx } from "@emotion/core"
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js"
import { Stripe, StripeElements } from "@stripe/stripe-js"
import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"

import { SimpleBillingData, useCurrentBillingInfo } from "../../hooks/useCurrentBillingInfo"
import { useDialogueConfirmation2 } from "../../hooks/useDialogConfirmation"
import { useMediaBreakpoints } from "../../hooks/useMediaBreakpoints"
import { useSiteAlerts } from "../../hooks/useSiteAlerts"
import stripeLogo from "../../images/stripeLogo.png"
import { Card } from "../../interfaceParts/Card"
import { CGButton } from "../../interfaceParts/CGButton"
import { ContactUsButton } from "../../interfaceParts/ContactUsButton"
import { FileboyImageOrLocalImage } from "../../interfaceParts/FileboyImageOrLocal"
import { MultiButton } from "../../interfaceParts/MultiButton"
import { PageSection, PageSectionSidebar } from "../../interfaceParts/PageSection"
import { NewPlanSelector, PlanSelector } from "../../interfaceParts/PlanSelector"
import { TextGroupMedium, TextGroupWithActions } from "../../interfaceParts/Title"
import { useApi } from "../../providers/ApiProvider"
import { useBillingStatus } from "../../providers/BillingStatusProvider"
import { IRecord } from "../../types/schema"
import { diffRecordsForUpdateEvent } from "../../utils/eventUtils"
import { useEvents } from "../../utils/useEvents"
import { SaaSTermsPage } from "./SaaSTermsPage"
import { Frame } from "../../interfaceParts/Frame"
import { useFetchMe } from "../../hooks/useFetchMe"
import { LoadingFrame } from "./UserResultsShowPage/AssessmentDetailsTab"
import { CompanySettingsPageWrapper } from "./CompanySettingsPageWrapper"
import { useAsyncEffect } from "../../hooks/useAsyncEffect"
import { useGlobalState } from "../../utils/globalState"
import { CompanyEditSubscriptionSection } from "../admin/AdminCompanyBillingPage"
import { icons } from "../../utils/icons"

type ICompany = IRecord<
  "companies",
  | "name"
  | "imageFileboyId"
  | "primaryColor"
  | "secondaryColor"
  | "users"
  | "planName"
  | "pricePennies"
  | "userLimit"
  | "email"
  | "addressLine1"
  | "addressLine2"
  | "addressLine3"
  | "addressCity"
  | "addressCounty"
  | "addressPostcode"
  | "chargeFrequency"
  | "charityNumber"
> & {
  plan: IRecord<
    "companyPlans",
    "name" | "pricePenniesPerUser" | "minUsers" | "maxUsers" | "includesOneHourHR"
  > | null
}

type IPlan =
  | {
      type: "old"
      plan: IRecord<
        "plans",
        | "name"
        | "pricePennies"
        | "userLimit"
        | "canWhiteLabel"
        | "canHaveTeams"
        | "chargeFrequency"
        | "oneHourHrPricePennies"
      > & { includesOneHourHR: boolean }
    }
  | {
      type: "perUser"
      plan: IRecord<"perUserPlans", "name" | "minUsers" | "maxUsers" | "pricePenniesPerUser"> & {
        includesOneHourHR: boolean
      }
    }

interface IProps {
  company: ICompany
  afterBillingSaved(): void
  refetchKey?: number
}

export function UserManageBillingInfo() {
  const globalState = useGlobalState()
  const [enableNewBillingDashboard, setEnableNewBillingDashboard] = useState<boolean | null>(null)

  useAsyncEffect(async () => {
    const company = await window.skeem.fetch2<IRecord<
      "companies",
      "enableBillingDashboard"
    > | null>({
      first: {
        name: "companies",
        attributes: ["enableBillingDashboard"],
        matches: { id: globalState.companyId },
      },
    })
    assert(company, "Company not found")
    setEnableNewBillingDashboard(company.enableBillingDashboard)
  }, [globalState.companyId])

  if (enableNewBillingDashboard === null) return <LoadingFrame />

  if (enableNewBillingDashboard) {
    return (
      <CompanySettingsPageWrapper
        active="billing"
        breadcrumb={{ name: "Billing", to: "#", icon: icons.billing }}
      >
        <CompanyEditSubscriptionSection companyId={globalState.companyId} />
      </CompanySettingsPageWrapper>
    )
  } else {
    return <UserManageBillingInfoOld />
  }
}
function UserManageBillingInfoOld() {
  const { onBillingStatusUpdate, reloadKey } = useBillingStatus()
  const user = useFetchMe([reloadKey])
  if (!user) return <LoadingFrame />

  return (
    <CompanySettingsPageWrapper
      active="billing"
      breadcrumb={{ name: "Billing", to: "#", icon: icons.billing }}
    >
      <UserManageBillingInfoInner
        company={user.company}
        afterBillingSaved={onBillingStatusUpdate}
        refetchKey={reloadKey}
      />
    </CompanySettingsPageWrapper>
  )
}
export function UserManageBillingInfoInner({ company, afterBillingSaved, refetchKey }: IProps) {
  const elements = useElements()
  const stripe = useStripe()
  const data = useCurrentBillingInfo([refetchKey])

  const [successModalOpen, setSuccessModalOpen] = useState(false)

  if (!data || !stripe || !elements) {
    return <LoadingFrame />
  }

  if (data.freeAccount) {
    return <Frame>This is a free account</Frame>
  }

  const props2: IProps2 = {
    afterBillingSaved,
    company,
    data,
    elements,
    setSuccessModalOpen,
    stripe,
  }

  return (
    <div>
      <SuccessModal open={successModalOpen} setOpen={setSuccessModalOpen} />
      {data.hasSubscription ? (
        <UserUpdateBillingInfo {...props2} />
      ) : (
        <UserNewBillingInfo {...props2} />
      )}
    </div>
  )
}
interface IProps2 {
  company: ICompany
  data: SimpleBillingData
  stripe: Stripe
  elements: StripeElements
  afterBillingSaved(): void
  setSuccessModalOpen(value: boolean): void
}

export function UserNewBillingInfo({
  company,
  stripe,
  elements,
  afterBillingSaved,
  setSuccessModalOpen,
}: IProps2) {
  const { onBillingStatusUpdate } = useBillingStatus()
  const api = useApi()
  const skeem = useSkeem()
  const alerts = useSiteAlerts()
  const theme = useTheme()
  const events = useEvents()
  const billingData = useCurrentBillingInfo()
  const [hasConfirmedSaas, setHasConfirmedSaas] = useState(false)

  const confirmSaaS = useDialogueConfirmation2<boolean, any>({
    width: 800,
    Component: ({ onResolve }) => (
      <div>
        <TextGroupMedium
          heading={`Setting up your plan.`}
          body={`You must accept the SaaS to continue setup your plan.`}
        />
        <Spacer />
        <div
          css={css`
            border: 1px solid #ddd;
            background: #f5f5f5;
            box-shadow: inset 0 0 4px rgba(0, 0, 0, 0.25);
            overflow: auto;
          `}
        >
          <SaaSTermsPage />
        </div>
        <Spacer />
        <MultiButton
          buttons={[
            () => <CGButton onClick={() => onResolve(true)}>Accept</CGButton>,
            () => (
              <CGButton onClick={() => onResolve(false)} buttonColor="white">
                Decline
              </CGButton>
            ),
          ]}
        />
      </div>
    ),
  })

  const [selectedPlan, setSelectedPlan] = useState<IPlan | null>(null)

  const [stage, setStage] = useState<"plan" | "details">("plan")

  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [stage])

  async function handleSubmitCompletePlan(company: ICompany) {
    if (!hasConfirmedSaas) {
      const confirmed = await confirmSaaS(null)
      if (!confirmed) {
        alerts.genericError("You must accept the SaaS to proceed.")
        return
      }
      setHasConfirmedSaas(true)
    }
    try {
      if (!selectedPlan) {
        throw new Error("Please select a plan")
      }
      const card = elements.getElement(CardElement)
      if (!card) {
        throw new Error("Trying to create payment method without card")
      }

      const mutationData: any = {
        email: company.email,
        addressLine1: company.addressLine1,
        addressLine2: company.addressLine2,
        addressLine3: company.addressLine3,
        addressCity: company.addressCity,
        addressCounty: company.addressCounty,
        addressPostcode: company.addressPostcode,
      }
      if (selectedPlan.type === "old") {
        mutationData.planName = selectedPlan.plan.name
        mutationData.pricePennies = selectedPlan.plan.pricePennies
        mutationData.chargeFrequency = selectedPlan.plan.chargeFrequency
        mutationData.userLimit = selectedPlan.plan.userLimit
        mutationData.useWhiteLabelling = selectedPlan.plan.canWhiteLabel
        mutationData.canHaveTeams = selectedPlan.plan.canHaveTeams
        mutationData.saasAccepted = true
      } else {
        mutationData.usingNewBilling = true

        const planMutationData = {
          name: selectedPlan.plan.name,
          minUsers: selectedPlan.plan.minUsers,
          maxUsers: selectedPlan.plan.maxUsers,
          pricePenniesPerUser: selectedPlan.plan.pricePenniesPerUser,
          includesOneHourHR: selectedPlan.plan.includesOneHourHR,
        }
        if (company.plan) {
          mutationData.plan = { update: { id: company.plan.id, ...planMutationData } }
        } else {
          mutationData.plan = { create: planMutationData }
        }
      }
      const updatedCompany = await skeem.mutate({
        companies: {
          update: {
            id: company.id,
            ...mutationData,
          },
        },
      })

      const diff = diffRecordsForUpdateEvent(company, updatedCompany, [
        "email",
        "addressLine1",
        "addressLine2",
        "addressLine3",
        "addressCity",
        "addressCounty",
        "addressPostcode",
        "planName",
        "pricePennies",
        "chargeFrequency",
        "userLimit",
        "useWhiteLabelling",
      ])

      if (diff.isDifferent) {
        await events.updateRecord("companies", company.id, diff.diff)
      }

      const clientSecret = await api.createStripeSetupIntent(updatedCompany.id)

      const response = await stripe.confirmCardSetup(clientSecret.setupIntentClientSecret, {
        payment_method: {
          card,
        },
      })

      if (response.error) {
        alerts.genericError(response.error.message)
        console.error(response.error)
      } else if (!response.setupIntent.payment_method) {
        console.error("No payment method returned from confirmCardPayment", response)
        alerts.genericError("An error occurred")
      } else {
        await api.setCompanyPaymentMethod(company.id, response.setupIntent.payment_method)
        await api.updateCompanySubscription(company.id)
        setSuccessModalOpen(true)
        alerts.subscriptionCreated()
        afterCompleteUpdate()
        localStorage.removeItem("signup-plan")
      }
    } catch (e) {
      console.error(e)
      alerts.genericError(e.message)
    }
  }

  function afterCompleteUpdate() {
    const cardElement = elements.getElement(CardElement)
    cardElement?.clear()
    setSelectedPlan(null)
    window.scrollTo({ top: 0 })
    onBillingStatusUpdate()
    afterBillingSaved()
  }

  const usesOldBilling = false

  return (
    <div>
      {stage === "plan" && (
        <div>
          <Card border>
            <H3>No active subscription</H3>
            {billingData?.freeAccount ? (
              <P>You are currently on a free plan.</P>
            ) : billingData?.hadASubscription ? (
              <P>
                Your previous subscription is no longer active. You need to reselect a plan to start
                using the platform again.
              </P>
            ) : (
              <P>
                To get started using the platform, you need to select your plan and enter valid
                billing information.
              </P>
            )}
          </Card>

          <Spacer />

          <PageSection
            sidebar={() => (
              <PageSectionSidebar
                heading="Your plan"
                body="Select your plan, you can change your plan at any point in the future. Plans last a minimum of 12 months with monthly billing."
              />
            )}
            displayAsColumn
          >
            {usesOldBilling ? (
              <PlanSelector
                onSelectPlan={plan => {
                  setSelectedPlan({ type: "old", plan })
                  setStage("details")
                }}
              />
            ) : (
              <NewPlanSelector
                onSelectPlan={plan => {
                  setSelectedPlan({ type: "perUser", plan })
                  setStage("details")
                }}
              />
            )}
          </PageSection>
        </div>
      )}
      {stage === "details" && (
        <PageSection
          sidebar={() => (
            <PageSectionSidebar
              heading="Billing details"
              body="Enter your company billing information"
            />
          )}
        >
          <Form2.Form initialData={company} onSubmit={handleSubmitCompletePlan}>
            <Form2.Text field="name" label="Company name" validations={{ presence: true }} />
            <Form2.Text field="email" label="Billing email" validations={{ presence: true }} />
            <Form2.Text
              field="addressLine1"
              label="Address Line 1"
              validations={{ presence: true }}
            />
            <Form2.Text field="addressLine2" label="Address Line 2" />
            <Form2.Text field="addressCity" />
            <Form2.Text field="addressCounty" />
            <Form2.Text field="addressPostcode" />
            <Card border secondary>
              <Grid columns={[{ fr: 1 }, 90]} gap={12}>
                <TextGroupSmall
                  heading="Enter your payment details"
                  body="All our payments are handled securely via Stripe."
                />
                <Image src={stripeLogo} />
              </Grid>
              <Spacer />
              <div
                css={css`
                  border: 1px solid ${theme.forms.border.color};
                  border-radius: ${theme.forms.border.radius}px;
                  padding: 16px;
                  background: white;
                `}
              >
                <CardElement
                  options={{
                    hidePostalCode: true,
                    style: {
                      base: {
                        fontSize: "16px",
                        color: "black",
                        "::placeholder": { color: "grey" },
                      },
                      invalid: { color: "red" },
                    },
                  }}
                />
              </div>
            </Card>
            <div css={css("display: flex")}>
              <Button leftIcon="arrow-left" onClick={() => setStage("plan")}>
                Back
              </Button>
              <div css={css("flex: 1")} />
              <Form2.Submit />
            </div>
          </Form2.Form>
        </PageSection>
      )}
    </div>
  )
}

export function UserUpdateBillingInfo({
  company,
  data,
  stripe,
  elements,
  afterBillingSaved,
}: IProps2) {
  const globalState = useGlobalState()
  const api = useApi()
  const skeem = useSkeem()
  const alerts = useSiteAlerts()
  const events = useEvents()
  const { isTablet } = useMediaBreakpoints()

  const [updatingPlan, setUpdatingPlan] = useState(false)
  const [cancellingPlan, setCancellingPlan] = useState(false)
  const [resumingPlan, setResumingPlan] = useState(false)
  const [cancelModelOpen, setCancelModelOpen] = useState(false)

  const [tab, setTab] = useState<"current" | "change" | "billing">(
    data.hasActiveSubscription ? "current" : "billing",
  )

  async function handleUpdateSubscription(selectedPlan: IPlan) {
    try {
      setUpdatingPlan(true)
      if (!selectedPlan) {
        throw new Error("Please select a plan")
      }
      if (selectedPlan.type === "perUser") {
        console.error("TODO")
        return
      }

      let pricePennies = selectedPlan.plan.pricePennies
      if (pricePennies && selectedPlan.plan.includesOneHourHR) {
        pricePennies += selectedPlan.plan.oneHourHrPricePennies ?? 0
      }

      const updatedCompany = await skeem.mutate({
        companies: {
          update: {
            id: company.id,
            planName: selectedPlan.plan.name,
            pricePennies: pricePennies,
            chargeFrequency: selectedPlan.plan.chargeFrequency,
            subscriptionIncludesOneHourForHr: selectedPlan.plan.includesOneHourHR,
            userLimit: selectedPlan.plan.userLimit,
            useWhiteLabelling: selectedPlan.plan.canWhiteLabel,
            canHaveTeams: selectedPlan.plan.canHaveTeams,
          },
        },
      })

      const diff = diffRecordsForUpdateEvent(company, updatedCompany, [
        "planName",
        "pricePennies",
        "chargeFrequency",
        "userLimit",
        "useWhiteLabelling",
      ])

      if (diff.isDifferent) {
        await events.updateRecord("companies", company.id, diff.diff)
      }

      await api.updateCompanySubscription(company.id)
      alerts.subscriptionChanged()
      afterBillingSaved()
    } catch (e) {
      console.error(e)
      alerts.genericError(e.message)
    } finally {
      setUpdatingPlan(false)
    }
  }

  async function handleCancelSubscription() {
    try {
      setCancellingPlan(true)

      await api.cancelCompanySubscription(company.id)

      alerts.subscriptionCancelled()
      afterBillingSaved()
    } catch (e) {
      console.error(e)
      alerts.genericError(e.message)
    } finally {
      setCancellingPlan(false)
    }
  }

  async function handleResumeSubscription() {
    try {
      setResumingPlan(true)

      await api.updateCompanySubscription(company.id)

      alerts.subscriptionChanged()
      afterBillingSaved()
    } catch (e) {
      console.error(e)
      alerts.genericError(e.message)
    } finally {
      setResumingPlan(false)
    }
  }

  const isSendingToApi = updatingPlan || cancellingPlan || resumingPlan

  if (data.freeAccount) {
    return <Frame>This is a free account</Frame>
  }

  return (
    <div>
      <CancelModal
        open={cancelModelOpen}
        setOpen={setCancelModelOpen}
        onCancel={handleCancelSubscription}
      />
      {!data.hasActiveSubscription && (
        <div css={css("background: #ffe083; padding: 8px 16px")}>
          <P>
            <Icon icon="exclamation-triangle" />
            <Spacer horizontal />
            There{"'"}s a problem with your saved payment method. Please update your card details.
          </P>
        </div>
      )}
      <PageSection
        sidebar={() => (
          <PageSectionSidebar
            heading="Plans and Payment"
            body="Manage your plan"
            actions={[
              {
                children: "Current plan",
                buttonStyle: tab === "current" ? "solid" : "text",
                disabled: isSendingToApi,
                onClick: () => setTab("current"),
              },
              // {
              //   children: "Change plan",
              //   buttonStyle: tab === "change" ? "solid" : "text",
              //   disabled: isSendingToApi,
              //   onClick: () => setTab("change"),
              // },
              {
                children: "Update billing details",
                buttonStyle: tab === "billing" ? "solid" : "text",
                disabled: isSendingToApi || !!data.subscriptionWillCancel,
                onClick: () => setTab("billing"),
              },
            ]}
            verticalActions
          />
        )}
      >
        {tab === "current" && (
          <Card secondary>
            <List>
              {data.subscriptionWillCancel && (
                <Card border>
                  <div>
                    Your current subscription will end on{" "}
                    <b>{formatDate(data.subscriptionWillCancel)}</b>
                  </div>
                  <Spacer />
                  <CGButton
                    disabled={isSendingToApi}
                    onClick={handleResumeSubscription}
                    buttonProps={
                      resumingPlan ? { renderLeft: () => <Spinner size={22} /> } : undefined
                    }
                  >
                    Stop cancellation
                  </CGButton>
                </Card>
              )}
              {data.nextPaymentDate && !data.subscriptionWillCancel && (
                <Card border>
                  <P>
                    Your next payment date is: <b>{formatDate(data.nextPaymentDate)}</b>
                    <br />
                    Your subscription was started:{" "}
                    <b>{data.subscriptionBegan ? formatDate(data.subscriptionBegan) : ""}</b> and is
                    for 1 year. <br />
                    {""}
                    The plan will renew automatically.
                  </P>
                </Card>
              )}
              <PlanCard
                plan={
                  company.plan
                    ? { type: "perUser", plan: company.plan }
                    : {
                        type: "old",
                        plan: {
                          name: company.planName,
                          pricePennies: company.pricePennies,
                          userLimit: company.userLimit,
                          canWhiteLabel: globalState.settings.useWhiteLabelling,
                          canHaveTeams: globalState.settings.canHaveTeams,
                          chargeFrequency: company.chargeFrequency,
                          includesOneHourHR: globalState.settings.subscriptionIncludesOneHourForHr,
                          oneHourHrPricePennies: 6000,
                          id: "1",
                        },
                      }
                }
              />
              <Card css={css("border: 1px solid #ddd")} border>
                <TextGroupMedium
                  heading="Change plans"
                  body="To discuss changing the level of your subscription, please get in touch"
                />
                <Spacer />
                <ContactUsButton />
              </Card>
            </List>
          </Card>
        )}
        {tab === "change" && (
          <ReloadingOverlay isReloading={isSendingToApi}>
            <PlanSelector
              onSelectPlan={plan => handleUpdateSubscription({ type: "old", plan })}
              thin={!isTablet}
              currentPlanDetails={{
                userLimit: company.userLimit ?? undefined,
                price: company.pricePennies ?? undefined,
              }}
            />
            {!data.subscriptionWillCancel && (
              <React.Fragment>
                <Spacer size={16} />
                <Card css={css("border: 1px solid #ddd")} border>
                  <P>To discuss any plan related issues, please get in touch.</P>
                  <ContactUsButton />
                </Card>
              </React.Fragment>
            )}
          </ReloadingOverlay>
        )}
        {tab === "billing" && (
          <BillingInfoForm
            company={company}
            stripe={stripe}
            elements={elements}
            afterBillingSaved={afterBillingSaved}
          />
        )}
      </PageSection>
    </div>
  )
}

export function BillingInfoForm({
  company,
  stripe,
  elements,
  afterBillingSaved,
}: {
  company: ICompany
  stripe: Stripe
  elements: StripeElements
  afterBillingSaved(): void
}) {
  const theme = useTheme()
  const skeem = useSkeem()
  const events = useEvents()
  const alerts = useSiteAlerts()
  const api = useApi()

  async function handleUpdateBillingDetails(company: ICompany) {
    try {
      const card = elements.getElement(CardElement)
      if (!card) {
        throw new Error("Trying to create payment method without card")
      }

      const updatedCompany = await skeem.mutate({
        companies: {
          update: {
            id: company.id,
            email: company.email,
            addressLine1: company.addressLine1,
            addressLine2: company.addressLine2,
            addressLine3: company.addressLine3,
            addressCity: company.addressCity,
            addressCounty: company.addressCounty,
            addressPostcode: company.addressPostcode,
          },
        },
      })

      const diff = diffRecordsForUpdateEvent(company, updatedCompany, [
        "email",
        "addressLine1",
        "addressLine2",
        "addressLine3",
        "addressCity",
        "addressCounty",
        "addressPostcode",
      ])

      if (diff.isDifferent) {
        await events.updateRecord("companies", company.id, diff.diff)
      }

      const clientSecret = await api.createStripeSetupIntent(updatedCompany.id)

      const response = await stripe.confirmCardSetup(clientSecret.setupIntentClientSecret, {
        payment_method: {
          card,
        },
      })

      if (response.error) {
        alerts.genericError(response.error.message)
        console.error(response.error)
      } else if (!response.setupIntent.payment_method) {
        console.error("No payment method returned from confirmCardPayment", response)
        alerts.genericError("An error occurred")
      } else {
        await api.setCompanyPaymentMethod(company.id, response.setupIntent.payment_method)
        alerts.billingInfoChanged()
        const cardElement = elements.getElement(CardElement)
        cardElement?.clear()
        afterBillingSaved()
      }
    } catch (e) {
      console.error(e)
      alerts.genericError(e.message)
    }
  }

  return (
    <Form2.Form initialData={company} onSubmit={handleUpdateBillingDetails}>
      <Form2.Text field="name" label="Company name" validations={{ presence: true }} />
      <Form2.Text field="email" label="Billing email" validations={{ presence: true }} />
      <Form2.Text field="addressLine1" label="Address Line 1" validations={{ presence: true }} />
      <Form2.Text field="addressLine2" label="Address Line 2" />
      <Form2.Text field="addressCity" />
      <Form2.Text field="addressCounty" />
      <Form2.Text field="addressPostcode" />
      <Card border secondary>
        <Grid columns={[{ fr: 1 }, 90]} gap={12}>
          <TextGroupSmall
            heading="Enter your payment details"
            body="All our payments are handled securely via Stripe."
          />
          <Image src={stripeLogo} />
        </Grid>
        <Spacer />
        <div
          css={css`
            border: 1px solid ${theme.forms.border.color};
            border-radius: ${theme.forms.border.radius}px;
            padding: 16px;
            background: white;
          `}
        >
          <CardElement
            options={{
              hidePostalCode: true,
              style: {
                base: {
                  fontSize: "16px",
                  color: "black",
                  "::placeholder": { color: "grey" },
                },
                invalid: { color: "red" },
              },
            }}
          />
        </div>
      </Card>
      <div>
        <Form2.Submit />
      </div>
    </Form2.Form>
  )
}

function PlanCard({
  onClick,
  isSelected,
  plan,
}: {
  onClick?(): void
  isSelected?: boolean
  plan: Omit<IPlan, "id">
}) {
  const theme = useTheme()
  const planKeyHeadingCss = css`
    font-size: 16px;
    font-weight: 600;
  `

  if (plan.type === "old") {
    const currentPlan = plan.plan as (IPlan & { type: "old" })["plan"]
    return (
      <Card
        onClick={onClick}
        css={css`
          border: 1px solid ${theme.themeColor(isSelected ? "primary" : "#ddd")};
          cursor: ${onClick ? "pointer" : "default"};
        `}
        border
      >
        <H2 color="primary">{currentPlan.name}</H2>
        <Spacer />
        <List>
          {currentPlan.pricePennies && (
            <div>
              <span css={planKeyHeadingCss}>£{(currentPlan.pricePennies / 100).toFixed(2)}</span>{" "}
              <span>ex VAT</span>
              <br />
              <span css={css("font-size: 12px")}>
                Per {currentPlan.chargeFrequency === "monthly" ? "month" : "year"}
              </span>{" "}
            </div>
          )}
          <div>
            <span css={planKeyHeadingCss}>{currentPlan.userLimit}</span>
            <br />
            <span css={css("font-size: 12px")}>
              {currentPlan.userLimit === 1 ? "User" : "Users"}
            </span>
          </div>
          {currentPlan.canWhiteLabel && (
            <div>
              <span css={planKeyHeadingCss}>Custom branding</span>
              <br />
              <span css={css("font-size: 12px")}>White label your portal</span>
            </div>
          )}
          {true && (
            <div>
              <span css={planKeyHeadingCss}>Team Management</span>
              <br />
              <span css={css("font-size: 12px")}>Create and manage custom teams</span>
            </div>
          )}
        </List>
      </Card>
    )
  } else {
    const currentPlan = plan.plan as (IPlan & { type: "perUser" })["plan"]
    return (
      <Card
        onClick={onClick}
        css={css`
          border: 1px solid ${theme.themeColor(isSelected ? "primary" : "#ddd")};
          cursor: ${onClick ? "pointer" : "default"};
        `}
        border
      >
        <H2 color="primary">{currentPlan.name}</H2>
        <Spacer />
        <List>
          {currentPlan.pricePenniesPerUser && (
            <div>
              <span css={planKeyHeadingCss}>
                £{(currentPlan.pricePenniesPerUser / 100).toFixed(2)}
              </span>{" "}
              <span>ex VAT</span>
              <br />
              <span css={css("font-size: 12px")}>Per user per month</span>{" "}
            </div>
          )}
          <div>
            <span css={planKeyHeadingCss}>Users</span>
            <br />
            <span css={css("font-size: 12px")}>
              {currentPlan.minUsers} - {currentPlan.maxUsers} Users
            </span>
          </div>
          <div>
            <span css={planKeyHeadingCss}>Custom branding</span>
            <br />
            <span css={css("font-size: 12px")}>White label your portal</span>
          </div>
          {currentPlan.includesOneHourHR && (
            <div>
              <span css={planKeyHeadingCss}>Monthly Support</span>
              <br />
              <span css={css("font-size: 12px")}>Includes 1 hour monthly support</span>
            </div>
          )}
          <div>
            <span css={planKeyHeadingCss}>Team Management</span>
            <br />
            <span css={css("font-size: 12px")}>Create and manage custom teams</span>
          </div>
        </List>
      </Card>
    )
  }
}

function SuccessModal({ open, setOpen }: { open: boolean; setOpen(open: boolean): void }) {
  return (
    <Modal
      onRequestClose={() => setOpen(false)}
      isOpen={open}
      width={500}
      css={css("padding: 0px !important")}
      render={() => (
        <div css={css("position: relative; overflow: hidden; padding: 16px")}>
          <TextGroupWithActions
            heading="Subscription successful!"
            body={
              <div>
                <P color="primary">
                  Thank you for starting your subscription. From here - you can invite your team
                  mates to sign up, or get started with the system.
                </P>
                <Spacer />
                <FileboyImageOrLocalImage
                  css={css("position: absolute; bottom: -20px; right: -30px; width: 150px")}
                  image={"hedgehog"}
                />
              </div>
            }
            size="medium"
            actions={[
              {
                children: (
                  <Link to="/" css={css("color: white; text-decoration: none")}>
                    Get Started
                  </Link>
                ),
                onClick: () => {},
              },
            ]}
          />
        </div>
      )}
    />
  )
}

function CancelModal({
  open,
  setOpen,
  onCancel,
}: {
  open: boolean
  setOpen(open: boolean): void
  onCancel(): void
}) {
  return (
    <Modal
      onRequestClose={() => setOpen(false)}
      isOpen={open}
      width={500}
      css={css("padding: 0px !important")}
      render={() => (
        <div css={css("position: relative; overflow: hidden; padding: 16px")}>
          <TextGroupWithActions
            heading="Are you sure you want to cancel?"
            body={
              <div>
                <P color="primary">
                  At the end of your current billing period, your company will lose access to the
                  platform.
                </P>
              </div>
            }
            size="medium"
            actions={[
              {
                children: "Cancel Subscription",
                buttonColor: "red",
                onClick: () => {
                  setOpen(false)
                  onCancel()
                },
              },
              {
                children: "Keep Subscription",
                onClick: () => setOpen(false),
              },
            ]}
          />
        </div>
      )}
    />
  )
}
